import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock, X, School, Vote, Sparkles, Sun, Sunrise } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { useActivities } from '@/hooks/useActivities';
import { useCategories } from '@/hooks/useCategories';
import { supabase } from '@/integrations/supabase/client';
import { optimizedImageUrl } from '@/lib/storage';
import { FavoriteButton } from '@/components/FavoriteButton';
import { HomeSearch } from '@/components/home/HomeSearch';


type TabId = 'today' | 'tomorrow' | 'schools';

function fmtTime(t: string, isArabic: boolean) {
  const [hStr, mStr] = t.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? (isArabic ? 'م' : 'PM') : (isArabic ? 'ص' : 'AM');
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

// Expand availability ranges into 30-min start times (e.g., 09:00 → 09:00, 09:30, 10:00 ... < end)
function expandRangesToSlots(
  ranges: { start_time: string; end_time: string }[],
  minStartMin: number = -1
): string[] {
  const out = new Set<string>();
  ranges.forEach((r) => {
    const [sh, sm] = String(r.start_time).split(':').map(Number);
    const [eh, em] = String(r.end_time).split(':').map(Number);
    let cur = (sh || 0) * 60 + (sm || 0);
    const end = (eh || 0) * 60 + (em || 0);
    while (cur + 30 <= end) {
      if (cur > minStartMin) {
        const h = Math.floor(cur / 60);
        const m = cur % 60;
        out.add(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
      cur += 30;
    }
  });
  return Array.from(out).sort();
}


export function TodayTomorrowSchoolsTabs() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const [tab, setTab] = useState<TabId>('today');
  const [openActivityId, setOpenActivityId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: activities = [] } = useActivities();
  const { data: categories = [] } = useCategories();

  const targetDate = useMemo(() => {
    const d = new Date();
    if (tab === 'tomorrow') d.setDate(d.getDate() + 1);
    return d;
  }, [tab]);

  const targetDow = targetDate.getDay();
  const todayDow = new Date().getDay();

  const { data: availability = [] } = useQuery({
    queryKey: ['instructor-availability-dow', targetDow],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_instructor_availability', { _day_of_week: targetDow });
      if (error) throw error;
      return data || [];
    },
    enabled: tab !== 'schools',
  });

  // Probe today availability so we can hide the "Today" tab when empty
  const { data: todayAvailability = [] } = useQuery({
    queryKey: ['instructor-availability-dow', todayDow, 'probe'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_public_instructor_availability', { _day_of_week: todayDow });
      if (error) throw error;
      return data || [];
    },
  });

  const availableInstructors = useMemo(
    () => new Set(availability.map((a: any) => a.instructor_id)),
    [availability]
  );
  const slotsByInstructor = useMemo(() => {
    const m: Record<string, { start_time: string; end_time: string }[]> = {};
    availability.forEach((a: any) => {
      (m[a.instructor_id] ||= []).push({ start_time: a.start_time, end_time: a.end_time });
    });
    return m;
  }, [availability]);

  // Schools with currently active polls
  const { data: schoolsWithActivePolls = [] } = useQuery({
    queryKey: ['schools-with-active-polls'],
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data: polls, error: pErr } = await supabase
        .from('school_polls')
        .select('school_id, ends_at, status')
        .eq('status', 'active')
        .gt('ends_at', nowIso);
      if (pErr) throw pErr;
      const ids = Array.from(new Set((polls || []).map((p: any) => p.school_id)));
      if (ids.length === 0) return [];
      const { data: schools, error: sErr } = await supabase
        .from('schools')
        .select('id, name_ar, name_en, logo_url, area, stage')
        .in('id', ids)
        .eq('is_published', true);
      if (sErr) throw sErr;
      // Earliest ends_at per school for countdown
      const endsBySchool: Record<string, string> = {};
      (polls || []).forEach((p: any) => {
        if (!endsBySchool[p.school_id] || p.ends_at < endsBySchool[p.school_id]) {
          endsBySchool[p.school_id] = p.ends_at;
        }
      });
      return (schools || []).map((s: any) => ({ ...s, ends_at: endsBySchool[s.id] }));
    },
    enabled: tab === 'schools',
  });

  const filteredActivities = useMemo(() => {
    if (tab === 'schools') return [];
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return activities.filter((a: any) => {
      if (!availableInstructors.has(a.instructor_id)) return false;
      const ranges = slotsByInstructor[a.instructor_id] || [];
      if (ranges.length === 0) return false;
      const minStart = tab === 'today' ? nowMin : -1;
      const expanded = expandRangesToSlots(ranges, minStart);
      return expanded.length > 0;
    });
  }, [activities, tab, availableInstructors, slotsByInstructor]);

  const [selectedSlot, setSelectedSlot] = useState<Record<string, string>>({});
  useEffect(() => { setSelectedSlot({}); }, [tab]);

  useEffect(() => {
    setOpenActivityId(null);
  }, [tab]);

  const scroll = (dir: 'prev' | 'next') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    const sign = dir === 'next' ? 1 : -1;
    el.scrollBy({ left: isArabic ? -sign * amount : sign * amount, behavior: 'smooth' });
  };

  // Determine if "today" has any candidate activities; hide tab and auto-switch when empty
  const todayHasActivities = useMemo(() => {
    if (!todayAvailability.length) return false;
    const instructors = new Set(todayAvailability.map((a: any) => a.instructor_id));
    const ranges: Record<string, { start_time: string; end_time: string }[]> = {};
    todayAvailability.forEach((a: any) => {
      (ranges[a.instructor_id] ||= []).push({ start_time: a.start_time, end_time: a.end_time });
    });
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return activities.some((a: any) => {
      if (!instructors.has(a.instructor_id)) return false;
      return expandRangesToSlots(ranges[a.instructor_id] || [], nowMin).length > 0;
    });
  }, [todayAvailability, activities]);

  // Track whether user manually selected a tab — if so, don't auto-override
  const userPickedTabRef = useRef(false);

  useEffect(() => {
    if (userPickedTabRef.current) return;
    if (todayHasActivities && tab !== 'today') {
      setTab('today');
    } else if (!todayHasActivities && tab === 'today') {
      setTab('tomorrow');
    }
  }, [todayHasActivities, tab]);

  const TABS: { id: TabId; ar: string; en: string }[] = [
    ...(todayHasActivities ? [{ id: 'today' as TabId, ar: 'اليوم', en: 'Today' }] : []),
    { id: 'tomorrow', ar: 'باجر', en: 'Tomorrow' },
  ];

  const isEmpty =
    tab === 'schools' ? schoolsWithActivePolls.length === 0 : filteredActivities.length === 0;

  const sectionBg =
    tab === 'today'
      ? 'bg-gradient-to-br from-orange-500/10 via-red-500/8 to-pink-600/10'
      : tab === 'tomorrow'
      ? 'bg-gradient-to-br from-indigo-600/10 via-purple-600/8 to-fuchsia-600/10'
      : '';

  return (
    <section className={`py-6 md:py-8 transition-[background-color,background-image] duration-700 ease-out rounded-3xl ${sectionBg}`}>
      <div className="container mx-auto px-4">


        {/* Tabs (one side) + Search (opposite side) */}
        <div className="flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-4 md:gap-8 mb-8">
          <div className="flex items-center justify-center md:justify-start gap-3 md:gap-4 shrink-0">
            {TABS.map((t) => {
              const active = tab === t.id;
              const activeColors =
                t.id === 'today'
                  ? 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 text-white border-white/50 shadow-[0_12px_32px_-6px_rgba(239,68,68,0.6),inset_0_1px_0_0_rgba(255,255,255,0.4)]'
                  : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white border-white/50 shadow-[0_12px_32px_-6px_rgba(147,51,234,0.6),inset_0_1px_0_0_rgba(255,255,255,0.4)]';
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { userPickedTabRef.current = true; setTab(t.id); }}
                  className={`relative shrink-0 inline-flex items-center justify-center rounded-full px-7 md:px-9 py-3 md:py-3.5 text-base md:text-lg font-extrabold transition-all duration-300 border-2 overflow-hidden group ${
                    active
                      ? `${activeColors} scale-105 animate-pulse-soft`
                      : 'bg-white/80 text-foreground border-white/90 backdrop-blur-xl shadow-[0_6px_20px_-6px_rgba(0,0,0,0.18)] hover:bg-white hover:scale-105'
                  }`}
                >
                  {active && (
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                  )}
                  <span className="relative z-10">{isArabic ? t.ar : t.en}</span>
                </button>
              );
            })}
          </div>
          <div className="flex-1 max-w-xl md:ms-auto">
            <HomeSearch />
          </div>
        </div>




        {isEmpty ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            {tab === 'schools'
              ? (isArabic ? 'لا توجد مدارس بتصويت جاري حالياً' : 'No schools with active votes')
              : (isArabic ? 'لا توجد أنشطة متاحة' : 'No activities available')}
          </p>
        ) : (
          <div key={tab} className="relative animate-fade-in">

            <button
              type="button"
              aria-label={isArabic ? 'السابق' : 'Previous'}
              onClick={() => scroll('prev')}
              className="hidden md:flex absolute start-0 top-[35%] -translate-y-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/90 backdrop-blur border border-border shadow-md"
            >
              <ChevronLeft className="h-5 w-5 rtl:hidden" />
              <ChevronRight className="h-5 w-5 hidden rtl:block" />
            </button>
            <button
              type="button"
              aria-label={isArabic ? 'التالي' : 'Next'}
              onClick={() => scroll('next')}
              className="hidden md:flex absolute end-0 top-[35%] -translate-y-1/2 translate-x-1/2 rtl:-translate-x-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/90 backdrop-blur border border-border shadow-md"
            >
              <ChevronRight className="h-5 w-5 rtl:hidden" />
              <ChevronLeft className="h-5 w-5 hidden rtl:block" />
            </button>

            <div
              ref={scrollRef}
              className="-mx-4 ps-4 pe-4 overflow-x-auto snap-x scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {tab === 'schools' ? (
                <div className="grid grid-flow-col auto-cols-[60%] sm:auto-cols-[40%] md:auto-cols-[calc((100%-5rem)/5)] gap-3 md:gap-4">
                  {schoolsWithActivePolls.map((s: any, i: number) => {
                    const name = isArabic ? s.name_ar : (s.name_en || s.name_ar);
                    return (
                      <Link
                        key={s.id}
                        to={`/schools/${s.id}`}
                        className="animate-fade-in snap-start group block"
                        style={{ animationDelay: `${i * 60}ms` }}
                      >
                        <div className="aspect-[4/5] overflow-hidden relative rounded-2xl bg-gradient-to-br from-primary/20 via-accent/20 to-lavender/30 flex items-center justify-center p-6">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />
                          {s.logo_url ? (
                            <img
                              src={optimizedImageUrl(s.logo_url, { width: 400, quality: 85 })}
                              alt={name}
                              loading="lazy"
                              className="max-w-[75%] max-h-[60%] object-contain drop-shadow-lg relative z-10"
                            />
                          ) : (
                            <School className="w-20 h-20 text-foreground/70 relative z-10" />
                          )}
                          <div className="absolute top-2 start-2 inline-flex items-center gap-1 rounded-full bg-red-500 text-white px-2 py-0.5 text-[10px] font-bold shadow-md animate-pulse">
                            <Vote className="w-3 h-3" />
                            {isArabic ? 'تصويت جاري' : 'Active Vote'}
                          </div>
                        </div>
                        <div className="pt-2 px-0.5">
                          <h3 className="text-sm md:text-base font-bold line-clamp-1 leading-tight">
                            {name}
                          </h3>
                          {s.area && (
                            <p className="text-[11px] md:text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {s.area}
                            </p>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-flow-col auto-cols-[45%] sm:auto-cols-[32%] md:auto-cols-[calc((100%-5rem)/5)] gap-3 md:gap-4">
                  {filteredActivities.map((a: any, i: number) => {
                    const title = isArabic ? a.title_ar : (a.title_en || a.title_ar);
                    const cat = categories.find((c) => c.id === a.category_id);
                    const catName = cat
                      ? isArabic ? cat.name_ar : (cat.name_en || cat.name_ar)
                      : '';
                    const imageUrl =
                      a.gallery_urls?.[0] ||
                      'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400';
                    const isOpen = openActivityId === a.id;
                    const ranges = slotsByInstructor[a.instructor_id] || [];
                    const now = new Date();
                    const nowMin = now.getHours() * 60 + now.getMinutes();
                    const visibleSlots = expandRangesToSlots(ranges, tab === 'today' ? nowMin : -1);
                    const chosen = selectedSlot[a.id];

                    return (
                      <div
                        key={a.id}
                        className="snap-start group block"
                      >
                        <div className="relative">
                          <div className="absolute top-2 end-2 z-20">
                            <FavoriteButton activityId={a.id} size="sm" />
                          </div>
                          <button
                            type="button"
                            onClick={() => setOpenActivityId(isOpen ? null : a.id)}
                            className="w-full text-start"
                          >
                            <div className="aspect-[4/5] overflow-hidden relative rounded-2xl bg-muted">
                              <img
                                src={optimizedImageUrl(imageUrl, { width: 500, quality: 70 })}
                                alt={title}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                              {catName && (
                                <span className="absolute top-2 start-2 z-10 inline-block text-[10px] md:text-xs font-medium text-[hsl(15_70%_28%)] bg-white/90 backdrop-blur px-2 py-0.5 rounded-full shadow-sm">
                                  {catName}
                                </span>
                              )}
                              <div className="absolute bottom-3 start-3 end-3 z-10">
                                <h3 className="text-white text-sm md:text-base font-extrabold drop-shadow-md line-clamp-1 leading-tight">
                                  {title}
                                </h3>
                                <p className="text-white/90 text-xs md:text-sm mt-0.5 drop-shadow">
                                  {a.price_kwd} {isArabic ? 'د.ك' : 'KWD'}
                                </p>
                              </div>
                            </div>
                            <div className="pt-2 px-0.5 flex justify-center">
                              <span className="inline-flex items-center gap-1 rounded-full bg-white/30 backdrop-blur-xl px-3 py-1 text-[11px] font-bold border border-white/50 shadow-[0_4px_16px_-4px_rgba(34,197,94,0.35),inset_0_1px_0_0_rgba(255,255,255,0.6)]">
                                <Clock className="w-3 h-3 text-green-600" />
                                <span className="text-green-700">{isArabic ? 'متوفّر' : 'Available'}</span>
                              </span>
                            </div>

                          </button>
                        </div>


                        {isOpen && (
                          <div className="mt-2 rounded-2xl bg-white/70 backdrop-blur-2xl border border-white/80 shadow-elevated p-3 animate-fade-in">
                            <div className="flex items-center justify-between mb-2">
                              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground">
                                <Clock className="w-3.5 h-3.5 text-primary" />
                                {isArabic
                                  ? tab === 'today' ? 'أوقات اليوم' : 'أوقات باجر'
                                  : tab === 'today' ? "Today's Times" : "Tomorrow's Times"}
                              </div>
                              <button
                                type="button"
                                onClick={() => setOpenActivityId(null)}
                                aria-label="close"
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {visibleSlots.length === 0 ? (
                              <p className="text-xs text-muted-foreground">
                                {isArabic ? 'لا توجد أوقات متاحة' : 'No available times'}
                              </p>
                            ) : (
                              <div className="grid grid-cols-3 gap-1.5 mb-2">
                                {visibleSlots.map((t) => {
                                  const isSel = chosen === t;
                                  return (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSlot((prev) => ({ ...prev, [a.id]: t }));
                                      }}
                                      className={
                                        'inline-flex items-center justify-center rounded-full backdrop-blur-md border text-[11px] font-semibold px-2 py-1 transition ' +
                                        (isSel
                                          ? 'bg-green-600 border-green-600 text-white shadow-[0_4px_12px_-2px_rgba(34,197,94,0.6)]'
                                          : 'bg-green-500/25 border-green-500/40 text-green-800 shadow-[0_2px_8px_-2px_rgba(34,197,94,0.45)] hover:bg-green-500/35')
                                      }
                                    >
                                      {fmtTime(t, isArabic)}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            <Link
                              to={
                                chosen
                                  ? `/activities/${a.id}?date=${tab === 'today' ? new Date().toISOString().slice(0,10) : new Date(Date.now()+86400000).toISOString().slice(0,10)}&time=${chosen}`
                                  : `/activities/${a.id}`
                              }
                              className={
                                'inline-flex w-full justify-center items-center rounded-full text-xs font-bold px-3 py-1.5 mt-1 transition ' +
                                (chosen
                                  ? 'bg-foreground text-background hover:opacity-90'
                                  : 'bg-foreground/40 text-background pointer-events-none')
                              }
                              aria-disabled={!chosen}
                            >
                              {isArabic ? 'احجز الآن' : 'Book Now'}
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
